/**
 * Biblioteca — /app/renata/knowledge
 */
import { RenataFeatureGate } from "@/components/renata/RenataFeatureGate";
import { RenataPageChrome } from "@/components/renata/RenataPageChrome";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { BookMarked, Search, RefreshCw, Database, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { clientRequestHeaders } from "@/lib/sm-api";

export const Route = createFileRoute("/app/renata/knowledge")({
  component: KnowledgePage,
  head: () => ({ meta: [{ title: "Biblioteca — Renata" }] }),
});

function KnowledgePage() {
  const tenantId = useWorkspaceTenantId();
  const [stats, setStats] = useState<Record<string, number>>({});
  const [embedding, setEmbedding] = useState<{
    kind?: string;
    labelEs?: string;
    detailEs?: string;
    engine?: string;
    model?: string;
  } | null>(null);
  const [sources, setSources] = useState<Array<Record<string, unknown>>>([]);
  const [layers, setLayers] = useState<Array<Record<string, unknown>>>([]);
  const [query, setQuery] = useState("¿Dónde está documentada la aprobación?");
  const [hits, setHits] = useState<Array<Record<string, unknown>>>([]);
  const [citations, setCitations] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    if (!tenantId) return;
    const res = await fetch("/api/renata/knowledge", {
      credentials: "include",
      headers: clientRequestHeaders(tenantId),
    });
    if (res.ok) {
      const d = await res.json();
      setStats(d.stats ?? {});
      setSources(d.sources ?? []);
      setLayers(d.layers ?? []);
      setEmbedding(d.embedding ?? null);
    }
  }

  useEffect(() => { void load(); }, [tenantId]);

  async function search(rag = false) {
    if (!tenantId) return;
    setBusy(true);
    try {
      const res = await fetch("/api/renata/knowledge", {
        method: "POST",
        credentials: "include",
        headers: { ...clientRequestHeaders(tenantId), "Content-Type": "application/json" },
        body: JSON.stringify({ action: rag ? "rag" : "search", query }),
      });
      const d = await res.json();
      if (rag) {
        setHits(d.rag?.hits ?? []);
        setCitations(d.citationsText ?? "");
      } else {
        setHits(d.hits ?? []);
        setCitations((d.citations ?? []).map((c: { sourceTitle: string }) => c.sourceTitle).join(", "));
      }
    } finally {
      setBusy(false);
    }
  }

  async function selfIndex() {
    if (!tenantId) return;
    setBusy(true);
    try {
      await fetch("/api/renata/knowledge", {
        method: "POST",
        credentials: "include",
        headers: { ...clientRequestHeaders(tenantId), "Content-Type": "application/json" },
        body: JSON.stringify({ action: "self_index" }),
      });
      await load();
    } finally {
      setBusy(false);
    }
  }

  return (
    <RenataFeatureGate featureId="knowledge">
    <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <BookMarked className="h-5 w-5 text-violet-600" /> Biblioteca
          </h1>
          <p className="text-sm text-muted-foreground">
            Biblioteca del negocio con búsqueda en documentación y citas. No es un chat con PDFs.
          </p>
          {embedding?.labelEs ? (
            <p className="mt-1 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{embedding.labelEs}</span>
              {" — "}
              {embedding.detailEs}
            </p>
          ) : null}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => void load()}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Actualizar
          </Button>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => void selfIndex()}>
            Indexar Structura
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {[
          ["Fuentes", stats.sources],
          ["Fragmentos", stats.chunks],
          [embedding?.kind === "production" ? "Índice (producción)" : "Índice (local/fallback)", stats.embeddings],
          ["Indexados", stats.indexed],
          ["Errores", stats.errors],
        ].map(([label, value]) => (
          <div key={String(label)} className="rounded-xl border p-3">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-xl font-bold tabular-nums">{value == null ? "—" : Number(value)}</p>
          </div>
        ))}
      </div>

      <div className="rounded-xl border p-4 space-y-2">
        <p className="text-xs font-medium flex items-center gap-1">
          <Layers className="h-3.5 w-3.5" /> Capas de la biblioteca
        </p>
        <div className="flex flex-wrap gap-1">
          {layers.map((l) => (
            <Badge key={String(l.id)} variant="secondary">{String(l.name)}</Badge>
          ))}
        </div>
      </div>

      <div className="rounded-xl border p-4 space-y-3">
        <label className="text-xs font-medium text-muted-foreground">Consultar conocimiento</label>
        <textarea
          className="w-full min-h-[72px] rounded-lg border bg-background px-3 py-2 text-sm"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="flex gap-2">
          <Button size="sm" disabled={busy} onClick={() => void search(false)}>
            <Search className="h-3.5 w-3.5 mr-1" /> Búsqueda combinada
          </Button>
          <Button size="sm" className="bg-violet-600 hover:bg-violet-700" disabled={busy} onClick={() => void search(true)}>
            <Database className="h-3.5 w-3.5 mr-1" /> Buscar con citas
          </Button>
        </div>
        {citations ? (
          <pre className="text-[11px] whitespace-pre-wrap rounded-lg bg-muted/30 p-2">{citations}</pre>
        ) : null}
        <div className="space-y-2">
          {hits.map((h, i) => {
            const chunk = (h.chunk as Record<string, unknown> | undefined) ?? h;
            const source = (h.source as Record<string, unknown> | undefined) ?? {};
            return (
              <div key={i} className="rounded-lg border px-3 py-2 text-sm">
                <div className="flex gap-2 items-center">
                  <span className="font-medium truncate">{String(source.title ?? "Hit")}</span>
                  <Badge variant="outline">{String((chunk.layer as string) ?? "")}</Badge>
                  {h.score != null ? (
                    <span className="text-[11px] text-muted-foreground">{Number(h.score).toFixed(2)}</span>
                  ) : null}
                </div>
                <p className="text-xs text-muted-foreground line-clamp-3 mt-1">
                  {String(chunk.text ?? "").slice(0, 280)}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <h2 className="text-sm font-semibold">Fuentes</h2>
        {sources.length === 0 && (
          <p className="text-sm text-muted-foreground">Sin fuentes. Indexá Structura o subí docs vía Captura/Bandeja.</p>
        )}
        {sources.map((s) => (
          <div key={String(s.id)} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
            <span className="font-medium flex-1 truncate">{String(s.title)}</span>
            <Badge variant="secondary">{String(s.scope)}</Badge>
            <Badge variant="outline">{String(s.status)}</Badge>
            <span className="text-[11px] text-muted-foreground">v{String(s.version)}</span>
          </div>
        ))}
      </div>
    </div>
    </RenataFeatureGate>
  );
}
